import axios from 'axios';

const DEFAULT_PROMPT_VALUE = 'default';

const getDefaultPromptOption = () => ({
  value: DEFAULT_PROMPT_VALUE,
  label: 'Default Prompt',
  description: 'Default report prompt',
  prompt: null,
  type: null,
  isDefault: true,
});

const normalizePromptOption = (prompt) => {
  if (!prompt || !prompt.id || !prompt.name || !prompt.prompt_start) {
    return null;
  }

  return {
    value: prompt.id,
    label: prompt.display_name || prompt.name,
    description: prompt.short_description || '',
    prompt: prompt.prompt_start.replaceAll(
      '[current-date]',
      new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    ),
    type: prompt.type || null,
    isDefault: false,
  };
};

export default {
  namespaced: true,

  state: () => ({
    prompts: [
      getDefaultPromptOption(),
    ],

    selectedImagesPrompt: DEFAULT_PROMPT_VALUE,
    selectedTitlesPrompt: DEFAULT_PROMPT_VALUE,

    isLoadingPrompts: false,
    isLoadingSettings: false,
    isSavingSettings: false,

    promptsError: null,
    settingsError: null,
  }),

  getters: {
    promptOptions(state) {
      return state.prompts && state.prompts.length
        ? state.prompts
        : [getDefaultPromptOption()];
    },

    selectedImagesPromptOption(state, getters) {
      return getters.promptOptions.find(
        option => option.value === state.selectedImagesPrompt
      ) || getDefaultPromptOption();
    },

    selectedTitlesPromptOption(state, getters) {
      return getters.promptOptions.find(
        option => option.value === state.selectedTitlesPrompt
      ) || getDefaultPromptOption();
    },

    getPromptByValue: (state, getters) => (value) => {
      return getters.promptOptions.find(option => option.value === value) || getDefaultPromptOption();
    },
  },

  mutations: {
    SET_PROMPTS(state, prompts) {
      state.prompts = [
        getDefaultPromptOption(),
        ...prompts,
      ];
    },

    SET_SELECTED_IMAGES_PROMPT(state, value) {
      state.selectedImagesPrompt = value || DEFAULT_PROMPT_VALUE;
    },

    SET_SELECTED_TITLES_PROMPT(state, value) {
      state.selectedTitlesPrompt = value || DEFAULT_PROMPT_VALUE;
    },

    SET_LOADING_PROMPTS(state, value) {
      state.isLoadingPrompts = value;
    },

    SET_LOADING_SETTINGS(state, value) {
      state.isLoadingSettings = value;
    },

    SET_SAVING_SETTINGS(state, value) {
      state.isSavingSettings = value;
    },

    SET_PROMPTS_ERROR(state, error) {
      state.promptsError = error;
    },

    SET_SETTINGS_ERROR(state, error) {
      state.settingsError = error;
    },
  },

  actions: {
    async loadPrompts({ commit }, payload = {}) {
      commit('SET_LOADING_PROMPTS', true);
      commit('SET_PROMPTS_ERROR', null);

      try {
        const response = await axios.get('/banner/groups/index_custom_prompts', {
          params: {
            type: payload.types || [],
            page: 1,
            paginate: false,
          },
        });

        const prompts = (response.data || [])
          .map(normalizePromptOption)
          .filter(Boolean);

        commit('SET_PROMPTS', prompts);
      } catch (error) {
        console.error('Error loading custom prompts:', error);
        commit('SET_PROMPTS_ERROR', error);
        commit('SET_PROMPTS', []);
      } finally {
        commit('SET_LOADING_PROMPTS', false);
      }
    },

    async loadSelectedPrompts({ commit }) {
      commit('SET_LOADING_SETTINGS', true);
      commit('SET_SETTINGS_ERROR', null);

      try {
        const response = await axios.get('/pdp-analysis/prompt-settings');
        
        let val = response.data?.value;
        
        if (val) {
          val = JSON.parse(val)
        }

        commit(
          'SET_SELECTED_IMAGES_PROMPT',
          val && val.images_prompt
            ? val.images_prompt
            : DEFAULT_PROMPT_VALUE
        );

        commit(
          'SET_SELECTED_TITLES_PROMPT',
          val && val.titles_prompt
            ? val.titles_prompt
            : DEFAULT_PROMPT_VALUE
        );
      } catch (error) {
        console.error('Error loading selected prompt settings:', error);
        commit('SET_SETTINGS_ERROR', error);

        commit('SET_SELECTED_IMAGES_PROMPT', DEFAULT_PROMPT_VALUE);
        commit('SET_SELECTED_TITLES_PROMPT', DEFAULT_PROMPT_VALUE);
      } finally {
        commit('SET_LOADING_SETTINGS', false);
      }
    },

    async saveSelectedPrompts({ state, commit }) {
      commit('SET_SAVING_SETTINGS', true);
      commit('SET_SETTINGS_ERROR', null);

      try {
        await axios.post('/pdp-analysis/prompt-settings', {
          images_prompt: state.selectedImagesPrompt,
          titles_prompt: state.selectedTitlesPrompt,
        });
      } catch (error) {
        console.error('Error saving selected prompt settings:', error);
        commit('SET_SETTINGS_ERROR', error);
        throw error;
      } finally {
        commit('SET_SAVING_SETTINGS', false);
      }
    },

    async updateImagesPrompt({ commit, dispatch }, value) {
      commit('SET_SELECTED_IMAGES_PROMPT', value);
      await dispatch('saveSelectedPrompts');
    },

    async updateTitlesPrompt({ commit, dispatch }, value) {
      commit('SET_SELECTED_TITLES_PROMPT', value);
      await dispatch('saveSelectedPrompts');
    },

    async updatePromptForTab({ dispatch }, { tab, value }) {
      if (tab === 'images') {
        await dispatch('updateImagesPrompt', value);
        return;
      }

      if (tab === 'titles') {
        await dispatch('updateTitlesPrompt', value);
      }
    },
  },
};
